import { useState, useEffect, useCallback, useRef } from "react";

// Tastiera MIDI on-screen a 25 tasti (2 ottave, C→C) per testare il device: manda
// NoteOn/NoteOff sul canale RX corrente. Suonabile con mouse/touch (drag = glissando
// monofonico) e con la tastiera del computer (layout DAW: a=C, w=C#, s=D…, polifonico).
// Controlli a sinistra: HOLD (latch: tiene tenute le note finché non le ripremi o
// togli HOLD) + shift di ottava. Velocity fissa.

const BLACK_PCS = new Set([1, 3, 6, 8, 10]); // pitch class dei tasti neri
const NUM_KEYS = 37; // 3 ottave (C→C)
const BASE_NOTE = 24; // C1 a shift 0 (default in basso, shift verso l'alto)
const VELOCITY = 127; // velocity di default
// BLACKWAVE accetta solo le note MIN_NOTE..MAX_NOTE (firmware midi_handlers.cpp); fuori
// range le ignora. Clampo l'ottava così che l'intera finestra [start, start+NUM-1] resti
// dentro [NOTE_LOW, NOTE_HIGH] → nessuna nota fuori range.
const NOTE_LOW = 24;
const NOTE_HIGH = 74;
const OCTAVE_MIN = Math.ceil((NOTE_LOW - BASE_NOTE) / 12);
const OCTAVE_MAX = Math.floor((NOTE_HIGH - (NUM_KEYS - 1) - BASE_NOTE) / 12);
const OCTAVE_LEVELS = Math.max(-OCTAVE_MIN, OCTAVE_MAX, 1); // per scalare l'intensità del giallo
// più ottave di shift = giallo più intenso (lightness che cala)
const octaveYellow = level => `hsl(50 100% ${Math.max(84 - ((level - 1) / OCTAVE_LEVELS) * 34, 46)}%)`;
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const noteName = note => `${NOTE_NAMES[note % 12]}${Math.floor(note / 12) - 1}`;

// Arpeggiator: direzioni ciclabili + rate (step al secondo) mappato dal controllo.
const ARP_DIRS = ["up", "down", "updown", "downup", "random"];
const ARP_DIR_LABEL = { up: "↑", down: "↓", updown: "↑↓", downup: "↓↑", random: "R" };
const ARP_DIR_TITLE = { up: "up", down: "down", updown: "up-down", downup: "down-up", random: "random" };
const ARP_MIN_HZ = 0.5;
const ARP_MAX_HZ = 16;
// controllo 1..127 -> 0.5..16 step/s (esponenziale, come la velocity ha corsa lineare)
const arpRateHz = v => ARP_MIN_HZ * Math.pow(ARP_MAX_HZ / ARP_MIN_HZ, (v - 1) / 126);

// tasto fisico → offset semitono dal C base (due file, come un controller DAW)
const KEY_TO_OFFSET = {
  a: 0, w: 1, s: 2, e: 3, d: 4, f: 5, t: 6, g: 7, y: 8, h: 9, u: 10, j: 11,
  k: 12, o: 13, l: 14, p: 15, ";": 16, "'": 17,
};

export default function MidiKeyboard({ output, channel, devMode }) {
  const [octave, setOctave] = useState(0);
  const [hold, setHold] = useState(false);
  const [velocity, setVelocity] = useState(VELOCITY);
  const [active, setActive] = useState(() => new Set()); // note MIDI attualmente suonanti (per l'highlight)
  const activeRef = useRef(active);
  const holdRef = useRef(hold);
  const velocityRef = useRef(velocity); // letta in noteOn senza rigenerare i listener
  const pointerDownRef = useRef(false);
  const pointerNoteRef = useRef(null); // nota suonata dal mouse (monofonica, solo momentaneo)
  const heldKeysRef = useRef({}); // tasto QWERTY → nota (per il keyup / anti-repeat)

  // --- Arpeggiator ---
  const [arpOn, setArpOn] = useState(false);
  const [arpRate, setArpRate] = useState(40); // 1..127 (come velocity), mappato a step/s
  const [arpGate, setArpGate] = useState(70); // 1..127 -> durata nota = frazione dello step
  const [arpDir, setArpDir] = useState("up");
  const [arpCurrent, setArpCurrent] = useState(null); // nota che l'arp sta suonando ora (highlight)
  const arpOnRef = useRef(arpOn);
  const arpDirRef = useRef(arpDir);
  const arpGateRef = useRef(arpGate); // letto nell'engine senza riavviare il timer
  const arpCurrentRef = useRef(null); // nota in corso (per lo spegnimento senza toccare il pool)
  const arpGateTimeoutRef = useRef(null); // spegnimento della nota a fine gate
  const arpIdxRef = useRef(0); // posizione nella sequenza
  const arpBounceRef = useRef(1); // direzione del rimbalzo (updown/downup): +1/-1
  useEffect(() => {
    arpOnRef.current = arpOn;
  }, [arpOn]);
  useEffect(() => {
    arpDirRef.current = arpDir;
  }, [arpDir]);
  useEffect(() => {
    arpGateRef.current = arpGate;
  }, [arpGate]);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);
  useEffect(() => {
    holdRef.current = hold;
  }, [hold]);
  useEffect(() => {
    velocityRef.current = velocity;
  }, [velocity]);

  const noteOn = useCallback(
    note => {
      setActive(prev => {
        if (prev.has(note)) return prev;
        const n = new Set(prev);
        n.add(note);
        return n;
      });
      if (devMode) {
        console.log(`DEV MODE — Note On ${note} (${noteName(note)}) vel ${velocityRef.current}`);
        return;
      }
      if (output) output.send([0x90 | ((channel - 1) & 0x0f), note & 0x7f, velocityRef.current & 0x7f]);
    },
    [output, channel, devMode]
  );

  const noteOff = useCallback(
    note => {
      setActive(prev => {
        if (!prev.has(note)) return prev;
        const n = new Set(prev);
        n.delete(note);
        return n;
      });
      if (devMode) {
        console.log(`DEV MODE — Note Off ${note} (${noteName(note)})`);
        return;
      }
      if (output) output.send([0x80 | ((channel - 1) & 0x0f), note & 0x7f, 0]);
    },
    [output, channel, devMode]
  );

  // spegne tutto (shift di ottava, HOLD off, smontaggio) → niente note appese
  const allNotesOff = useCallback(() => {
    setActive(prev => {
      if (!devMode && output) prev.forEach(note => output.send([0x80 | ((channel - 1) & 0x0f), note & 0x7f, 0]));
      return new Set();
    });
    pointerNoteRef.current = null;
    heldKeysRef.current = {};
  }, [output, channel, devMode]);

  // cambio ottava o smontaggio → spegni le note tenute (cleanup del effect)
  useEffect(() => allNotesOff, [octave, allNotesOff]);

  // in HOLD: premere una nota la latcha, ripremerla la rilascia (toggle)
  const toggleNote = useCallback(
    note => {
      if (activeRef.current.has(note)) noteOff(note);
      else noteOn(note);
    },
    [noteOn, noteOff]
  );

  const handleHoldToggle = () => {
    setHold(h => {
      if (h) allNotesOff(); // spegnendo HOLD rilascia tutte le note latchate
      return !h;
    });
  };

  // Arp: in modalità arpeggiatore i tasti NON suonano direttamente, ma entrano/escono
  // dal "pool" (Set active). L'arp li suona uno alla volta. Toggle senza inviare MIDI.
  const toggleArpPool = useCallback(note => {
    setActive(prev => {
      const n = new Set(prev);
      if (n.has(note)) n.delete(note);
      else n.add(note);
      return n;
    });
  }, []);

  // Motore arp: timer al rate scelto; a ogni step spegne la nota precedente e suona la
  // successiva del pool nella direzione scelta. Legge pool/dir/velocity dai ref (non
  // riavvia il timer a ogni cambio di pool). Il pool è il Set active (note latchate).
  useEffect(() => {
    if (!arpOn) return undefined;
    const stepMs = 1000 / arpRateHz(arpRate);
    const sendOn = note => {
      if (!devMode && output) output.send([0x90 | ((channel - 1) & 0x0f), note & 0x7f, velocityRef.current & 0x7f]);
    };
    const sendOff = note => {
      if (!devMode && output) output.send([0x80 | ((channel - 1) & 0x0f), note & 0x7f, 0]);
    };
    const tick = () => {
      // safety: se una nota è ancora accesa (gate ~100%), spegnila; annulla il gate pendente
      if (arpGateTimeoutRef.current) {
        clearTimeout(arpGateTimeoutRef.current);
        arpGateTimeoutRef.current = null;
      }
      if (arpCurrentRef.current != null) {
        sendOff(arpCurrentRef.current);
        arpCurrentRef.current = null;
      }
      const pool = [...activeRef.current].sort((a, b) => a - b);
      const len = pool.length;
      if (len === 0) {
        setArpCurrent(null);
        return;
      }
      let idx = arpIdxRef.current;
      const dir = arpDirRef.current;
      if (len === 1) {
        idx = 0;
      } else if (dir === "down") {
        idx = (idx - 1 + len) % len;
      } else if (dir === "random") {
        idx = Math.floor(Math.random() * len);
      } else if (dir === "updown" || dir === "downup") {
        let b = arpBounceRef.current;
        idx += b;
        if (idx >= len) {
          idx = len - 2;
          b = -1;
        } else if (idx < 0) {
          idx = 1;
          b = 1;
        }
        arpBounceRef.current = b;
      } else {
        idx = (idx + 1) % len; // up
      }
      idx = Math.max(0, Math.min(len - 1, idx));
      arpIdxRef.current = idx;
      const note = pool[idx];
      sendOn(note);
      arpCurrentRef.current = note;
      setArpCurrent(note);
      // gate: spegni la nota dopo una frazione dello step (durata regolabile)
      const gateMs = Math.max(20, (arpGateRef.current / 127) * stepMs);
      arpGateTimeoutRef.current = setTimeout(() => {
        if (arpCurrentRef.current === note) {
          sendOff(note);
          arpCurrentRef.current = null;
          setArpCurrent(null);
        }
        arpGateTimeoutRef.current = null;
      }, gateMs);
    };
    const id = setInterval(tick, stepMs);
    return () => {
      clearInterval(id);
      if (arpGateTimeoutRef.current) {
        clearTimeout(arpGateTimeoutRef.current);
        arpGateTimeoutRef.current = null;
      }
      if (arpCurrentRef.current != null) {
        sendOff(arpCurrentRef.current);
        arpCurrentRef.current = null;
      }
      setArpCurrent(null);
    };
  }, [arpOn, arpRate, output, channel, devMode]);

  const handleArpToggle = () => {
    if (arpOn) {
      setArpOn(false);
      allNotesOff(); // svuota il pool e spegne tutto
    } else {
      setHold(true); // l'arp forza il latch dei tasti
      arpIdxRef.current = -1;
      arpBounceRef.current = arpDir === "downup" ? -1 : 1;
      setArpOn(true);
    }
  };

  const cycleArpDir = () =>
    setArpDir(d => {
      const nd = ARP_DIRS[(ARP_DIRS.indexOf(d) + 1) % ARP_DIRS.length];
      arpBounceRef.current = nd === "downup" ? -1 : 1;
      return nd;
    });

  // mouse: in momentaneo drag monofonico; in HOLD toggle per tasto (niente drag)
  const playPointer = useCallback(
    note => {
      if (pointerNoteRef.current === note) return;
      if (pointerNoteRef.current != null) noteOff(pointerNoteRef.current);
      pointerNoteRef.current = note;
      noteOn(note);
    },
    [noteOn, noteOff]
  );

  useEffect(() => {
    const up = () => {
      pointerDownRef.current = false;
      if (pointerNoteRef.current != null) {
        noteOff(pointerNoteRef.current);
        pointerNoteRef.current = null;
      }
    };
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  }, [noteOff]);

  // tastiera del computer; ignora se il focus è su un campo di input
  useEffect(() => {
    const isFormEl = el => el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName);
    const down = e => {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || isFormEl(e.target)) return;
      const off = KEY_TO_OFFSET[e.key.toLowerCase()];
      if (off == null) return;
      const k = e.key.toLowerCase();
      if (heldKeysRef.current[k] != null) return; // già premuto (anti-repeat)
      const note = BASE_NOTE + octave * 12 + off;
      heldKeysRef.current[k] = note;
      if (arpOnRef.current) toggleArpPool(note); // arp: la nota entra/esce dal pool
      else if (holdRef.current) toggleNote(note);
      else noteOn(note);
    };
    const up = e => {
      const k = e.key.toLowerCase();
      const note = heldKeysRef.current[k];
      if (note == null) return;
      delete heldKeysRef.current[k];
      if (!holdRef.current) noteOff(note); // in HOLD la nota resta latchata
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, [octave, noteOn, noteOff, toggleNote, toggleArpPool]);

  const startNote = BASE_NOTE + octave * 12;
  const notes = Array.from({ length: NUM_KEYS }, (_, i) => startNote + i);
  let whiteCount = 0;
  const layout = notes.map(note => {
    const black = BLACK_PCS.has(note % 12);
    const item = { note, black, whiteBefore: whiteCount };
    if (!black) whiteCount += 1;
    return item;
  });
  const whiteW = 100 / whiteCount; // larghezza di un tasto bianco in %

  // velocity come "pulsante" con riempimento: drag/click orizzontale imposta 1..127
  const velDraggingRef = useRef(false);
  const setVelFromEvent = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setVelocity(Math.max(1, Math.min(127, Math.round(ratio * 127))));
  };
  const onVelDown = e => {
    e.preventDefault();
    velDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setVelFromEvent(e);
  };
  const onVelMove = e => {
    if (velDraggingRef.current) setVelFromEvent(e);
  };
  const onVelUp = e => {
    velDraggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // rate dell'arp: stesso "pulsante con riempimento" della velocity (drag orizzontale)
  const arpRateDraggingRef = useRef(false);
  const setArpRateFromEvent = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setArpRate(Math.max(1, Math.min(127, Math.round(ratio * 127))));
  };
  const onArpRateDown = e => {
    e.preventDefault();
    arpRateDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setArpRateFromEvent(e);
  };
  const onArpRateMove = e => {
    if (arpRateDraggingRef.current) setArpRateFromEvent(e);
  };
  const onArpRateUp = e => {
    arpRateDraggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  // gate dell'arp: stesso controllo a riempimento (durata nota = frazione dello step)
  const arpGateDraggingRef = useRef(false);
  const setArpGateFromEvent = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    setArpGate(Math.max(1, Math.min(127, Math.round(ratio * 127))));
  };
  const onArpGateDown = e => {
    e.preventDefault();
    arpGateDraggingRef.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    setArpGateFromEvent(e);
  };
  const onArpGateMove = e => {
    if (arpGateDraggingRef.current) setArpGateFromEvent(e);
  };
  const onArpGateUp = e => {
    arpGateDraggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const keyHandlers = note => ({
    onPointerDown: e => {
      e.preventDefault();
      if (arpOn) {
        toggleArpPool(note); // arp: aggiunge/toglie la nota dal pool, non suona diretto
        return;
      }
      if (hold) {
        toggleNote(note);
        return;
      }
      pointerDownRef.current = true;
      playPointer(note);
    },
    onPointerEnter: () => {
      if (!hold && !arpOn && pointerDownRef.current) playPointer(note);
    },
  });

  return (
    <div className="bw-kbd">
      {/* Colonna arpeggiator: ON (forza il latch), RATE (drag come velocity), DIR */}
      <div className="bw-kbd__controls">
        <button
          type="button"
          className={`bw-kbd__ctrl${arpOn ? " is-active" : ""}`}
          onClick={handleArpToggle}
          title="Arpeggiator on/off"
        >
          Arp{arpOn ? " ●" : ""}
        </button>
        <div
          className="bw-kbd__vel"
          role="slider"
          aria-label="Arp rate"
          aria-valuemin={1}
          aria-valuemax={127}
          aria-valuenow={arpRate}
          onPointerDown={onArpRateDown}
          onPointerMove={onArpRateMove}
          onPointerUp={onArpRateUp}
          style={{
            background: `linear-gradient(to right, var(--color-yellow) ${(arpRate / 127) * 100}%, var(--color-gray-5) ${(arpRate / 127) * 100}%)`,
          }}
        >
          Rate
        </div>
        <div
          className="bw-kbd__vel"
          role="slider"
          aria-label="Arp gate"
          aria-valuemin={1}
          aria-valuemax={127}
          aria-valuenow={arpGate}
          onPointerDown={onArpGateDown}
          onPointerMove={onArpGateMove}
          onPointerUp={onArpGateUp}
          style={{
            background: `linear-gradient(to right, var(--color-yellow) ${(arpGate / 127) * 100}%, var(--color-gray-5) ${(arpGate / 127) * 100}%)`,
          }}
        >
          Gate
        </div>
        <button
          type="button"
          className="bw-kbd__ctrl"
          onClick={cycleArpDir}
          disabled={!arpOn}
          title={`Direction: ${ARP_DIR_TITLE[arpDir]}`}
        >
          Dir {ARP_DIR_LABEL[arpDir]}
        </button>
      </div>

      <div className="bw-kbd__controls">
        <button
          type="button"
          className={`bw-kbd__ctrl bw-kbd__hold${hold ? " is-active" : ""}`}
          onClick={handleHoldToggle}
          disabled={arpOn}
        >
          Hold
        </button>
        <button
          type="button"
          className={`bw-kbd__ctrl${octave > 0 ? " is-active" : ""}`}
          style={octave > 0 ? { background: octaveYellow(octave) } : undefined}
          onClick={() => setOctave(o => Math.min(o + 1, OCTAVE_MAX))}
          disabled={octave >= OCTAVE_MAX}
        >
          Oct +
        </button>
        <button
          type="button"
          className={`bw-kbd__ctrl${octave < 0 ? " is-active" : ""}`}
          style={octave < 0 ? { background: octaveYellow(-octave) } : undefined}
          onClick={() => setOctave(o => Math.max(o - 1, OCTAVE_MIN))}
          disabled={octave <= OCTAVE_MIN}
        >
          Oct −
        </button>
        <div
          className="bw-kbd__vel"
          role="slider"
          aria-label="Velocity"
          aria-valuemin={1}
          aria-valuemax={127}
          aria-valuenow={velocity}
          onPointerDown={onVelDown}
          onPointerMove={onVelMove}
          onPointerUp={onVelUp}
          style={{
            background: `linear-gradient(to right, var(--color-yellow) ${(velocity / 127) * 100}%, var(--color-gray-5) ${(velocity / 127) * 100}%)`,
          }}
        >
          Velocity
        </div>
      </div>

      <div className="bw-kbd__keys">
        {layout
          .filter(k => !k.black)
          .map(k => (
            <button
              key={k.note}
              type="button"
              className={`bw-kbd__white${active.has(k.note) ? " is-active" : ""}${arpCurrent === k.note ? " is-arp" : ""}`}
              title={noteName(k.note)}
              {...keyHandlers(k.note)}
            />
          ))}
        {layout
          .filter(k => k.black)
          .map(k => (
            <button
              key={k.note}
              type="button"
              className={`bw-kbd__black${active.has(k.note) ? " is-active" : ""}${arpCurrent === k.note ? " is-arp" : ""}`}
              title={noteName(k.note)}
              style={{ left: `${k.whiteBefore * whiteW}%`, width: `${whiteW * 0.62}%` }}
              {...keyHandlers(k.note)}
            />
          ))}
      </div>
    </div>
  );
}
